import React, { useState, useEffect } from "react";
import LogicViewer from "./LogicViewer";
import {
  FaTrashCan,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeftLong,
  FaPlus,
} from "react-icons/fa6"; // Importing icons for collapse/expand functionality
import { SERVER_URL } from "./SERVER_URL";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const CreateForm = () => {
  const navigate = useNavigate();
  // State variables
  const [questions, setQuestions] = useState([]); // Holds all the questions
  const [selectedCategory, setSelectedCategory] = useState(""); // Selected main category
  const [selectedSubCategory, setSelectedSubCategory] = useState(""); // Selected sub-category
  const [selectedYear, setSelectedYear] = useState(""); // Selected year
  const [collapsedQuestions, setCollapsedQuestions] = useState({}); // Tracks collapsed state of questions
  const [headers, setHeaders] = useState([]); // Holds header tags
  const [headerSelections, setHeaderSelections] = useState({}); // temp selection per question id
  const [editingHeaderId, setEditingHeaderId] = useState(null);
  const [editingHeaderTitle, setEditingHeaderTitle] = useState("");

  // Ensure a default header section exists
  useEffect(() => {
    setHeaders((prev) => {
      if (prev.length > 0) return prev;
      return [{ id: Date.now(), title: "Sample Header" }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Categories and sub-categories
  const categories = {
    WASH: [
      "WASH Committee",
      "Community",
      "Adolescent Girls",
      "Health Officials",
    ],
    "Food Security": ["Student", "Food Distribution", "Farmers","Food Distribution Bread Factory"],
    Education: ["Principal", "Orphan Students", "Teacher", "Parent Guardian","General Orphans"],
    Emergencies: ["EWS member v5", "Community of Disaster hit areas"],
    Health: [
      "Midwife",
      "Community(Women)",
      "Health Officials",
      "Community",
      "Medical Camp Beneficiary",
    ],
    Livelihood: ["Farmer", "Trainee"],
  };

  // Predefined forms for certain categories and sub-categories
  const predefinedForms = {
    WASH: {
      "WASH Committee": [
        {
          id: 1,
          text: "Interviewers Details: Name",
          type: "text",
          answers: [],
        },
        { id: 2, text: "Designation", type: "text", answers: [] },
        { id: 3, text: "Department", type: "text", answers: [] },
        { id: 4, text: "Location", type: "text", answers: [] },
        { id: 5, text: "Date", type: "text", answers: [] },
        { id: 6, text: "Contact #", type: "text", answers: [] },
        { id: 7, text: "Respondent Details: Name", type: "text", answers: [] },
        { id: 8, text: "Country", type: "text", answers: [] },
        { id: 9, text: "District", type: "text", answers: [] },
        { id: 10, text: "Area", type: "text", answers: [] },
        { id: 11, text: "Contact #", type: "text", answers: [] },
        {
          id: 12,
          text: "Age",
          type: "checkbox",
          answers: [
            { text: "18-24", nextQuestion: null },
            { text: "25-34", nextQuestion: null },
            { text: "35-44", nextQuestion: null },
            { text: "45-54", nextQuestion: null },
            { text: "55-64", nextQuestion: null },
            { text: "65 Above", nextQuestion: null },
          ],
        },
        {
          id: 13,
          text: "Gender",
          type: "checkbox",
          answers: [
            { text: "Male", nextQuestion: null },
            { text: "Female", nextQuestion: null },
          ],
        },
      ],
      Community: [
        { id: 1, text: "Community Name", type: "text", answers: [] },
        { id: 2, text: "Population", type: "text", answers: [] },
        { id: 3, text: "Location", type: "text", answers: [] },
      ],
    },
  };

  // Function to toggle the collapsed state of a question
  const toggleCollapse = (id) => {
    setCollapsedQuestions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Function to add a new question
  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { id: prev.length + 1, text: "", type: "text", answers: [] },
    ]);
    toast.info("New question added");
  };

  // Header helpers
  const addHeaderSection = () => {
    const nextIndex = headers.length + 1;
    const title = `Header ${nextIndex}`;
    setHeaders((prev) => [...prev, { id: Date.now(), title }]);
    toast.success("Header added");
  };

  const removeHeader = (headerId) => {
    setHeaders((prev) => prev.filter((h) => h.id !== headerId));
    setQuestions((prev) =>
      prev.map((q) => (q.headerId === headerId ? { ...q, headerId: undefined } : q))
    );
    setHeaderSelections((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((qid) => {
        if (String(next[qid]) === String(headerId)) {
          delete next[qid];
        }
      });
      return next;
    });
    toast.info("Header removed");
  };

  const startEditHeader = (header) => {
    setEditingHeaderId(header.id);
    setEditingHeaderTitle(header.title);
  };

  const commitEditHeader = () => {
    const title = (editingHeaderTitle || "").trim();
    if (!title) {
      toast.error("Header title cannot be empty");
      return;
    }
    setHeaders((prev) => prev.map((h) => (h.id === editingHeaderId ? { ...h, title } : h)));
    setEditingHeaderId(null);
    setEditingHeaderTitle("");
    toast.success("Header updated");
  };

  const cancelEditHeader = () => {
    setEditingHeaderId(null);
    setEditingHeaderTitle("");
  };

  const addQuestionToHeader = (headerId) => {
    setQuestions((prev) => [
      ...prev,
      { id: prev.length + 1, text: "", type: "text", answers: [], headerId },
    ]);
    toast.info("New question added to section");
  };

  const attachHeaderToQuestion = (qIndex) => {
    const q = questions[qIndex];
    const selected = headerSelections[q.id];
    if (!selected) {
      toast.error("Select a header first");
      return;
    }
    const updatedQuestions = [...questions];
    updatedQuestions[qIndex] = { ...q, headerId: Number(selected) };
    setQuestions(updatedQuestions);
    toast.success("Header attached to question");
  };

  // Function to remove a question
  const removeQuestion = (qIndex) => {
    setQuestions((prev) => prev.filter((_, index) => index !== qIndex));
    toast.info("Question removed");
  };

  // Function to load predefined form based on selected category and sub-category
  const updateFormForSelection = () => {
    if (selectedSubCategory) {
      const prebuiltForm =
        predefinedForms[selectedCategory]?.[selectedSubCategory] || [];
      if (prebuiltForm.length === 0) {
        toast.warning("No predefined form available for this selection");
        return;
      }
      setQuestions(prebuiltForm);
      toast.success("Predefined form loaded successfully");
    }
  };

  // Function to update the text of a question
  const updateQuestionText = (index, newText) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].text = newText;
    setQuestions(updatedQuestions);
  };

  // Function to add an answer option to a question
  const addAnswerOption = (qIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[qIndex].answers.push({ text: "", nextQuestion: null });
    setQuestions(updatedQuestions);
  };

  // Function to update an answer option
  const updateAnswerOption = (qIndex, aIndex, newText) => {
    const updatedQuestions = [...questions];
    updatedQuestions[qIndex].answers[aIndex].text = newText;
    setQuestions(updatedQuestions);
  };

  // Function to remove an answer option
  const removeAnswerOption = (qIndex, aIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[qIndex].answers.splice(aIndex, 1);
    setQuestions(updatedQuestions);
  };

  // Function to update the next question ID for an answer option
  const updateNextQuestion = (qIndex, aIndex, nextQuestionId) => {
    const updatedQuestions = [...questions];
    updatedQuestions[qIndex].answers[aIndex].nextQuestion = nextQuestionId;
    setQuestions(updatedQuestions);
  };

  // Function to publish the form (send the form JSON to the API)
  const publishForm = async () => {
    // Validate required fields
    if (!selectedCategory || !selectedSubCategory || !selectedYear) {
      toast.error("Please select Category, Sub-Category, and Year");
      return;
    }

    if (questions.length === 0) {
      toast.error("Please add at least one question to the form");
      return;
    }

    const formData = {
      category: selectedCategory,
      sub_category: selectedSubCategory,
      year: selectedYear,
      questions: questions.map((q) => ({
        ...q,
        headerId: q.headerId ?? null,
        headerName:
          headers.find((h) => String(h.id) === String(q.headerId))?.title || null,
      })),
    };

    try {
      toast.info("Submitting form...", {
        autoClose: false,
        toastId: "submitForm",
      });

      const response = await fetch(`${SERVER_URL}/submit_questions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      // Dismiss the loading toast
      toast.dismiss("submitForm");

      if (response.ok) {
        const data = await response.json();
        toast.success("Form published successfully!");

        // Reset form after successful submission
        setSelectedCategory("");
        setSelectedSubCategory("");
        setSelectedYear("");
        setQuestions([]);
      } else {
        const errorData = await response.json();
        toast.error(
          errorData.message || "Failed to publish form. Please try again."
        );
      }
    } catch (error) {
      toast.dismiss("submitForm");
      toast.error("Network error. Please check your connection and try again.");
      console.error("Error:", error);
    }
  };

  const renderQuestionBlock = (question, qIndex) => (
    <div
      key={qIndex}
      className="relative mb-6 p-4 bg-gray-100 border border-gray-300 rounded-lg"
    >
      {/* Collapse Icon */}
      <div
        className="absolute top-2 right-2 cursor-pointer"
        onClick={() => toggleCollapse(question.id)}
      >
        {collapsedQuestions[question.id] ? (
          <FaChevronDown />
        ) : (
          <FaChevronUp />
        )}
      </div>

      {/* Collapsed View: Show Question ID and Text */}
      {collapsedQuestions[question.id] ? (
        <div>
          <p className="text-sm font-medium text-gray-700">Question ID: {question.id}</p>
          <p className="text-sm font-medium text-gray-700">
            Question Text: {question.text || "No text entered"}
          </p>
        </div>
      ) : (
        <>
          {/* Question Details */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question ID: {question.id}
          </label>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question Text
          </label>
          <textarea
            placeholder="Enter question text"
            value={question.text}
            onChange={(e) => updateQuestionText(qIndex, e.target.value)}
            className="block w-full p-3 mb-4 border border-gray-300 rounded-lg focus:ring focus:ring-indigo-200"
            rows={3}
          />

          {/* Answer Type Selection */}
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Answer Type
          </label>
          <select
            value={question.type}
            onChange={(e) =>
              setQuestions((prev) =>
                prev.map((q, i) => (i === qIndex ? { ...q, type: e.target.value } : q))
              )
            }
            className="block w-full p-2 mb-4 border border-gray-300 rounded-lg focus:ring focus:ring-indigo-200"
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="checkbox">Checkbox</option>
            <option value="radio">Radio Button options</option>
            <option value="date">Date</option>
            <option value="time">Time</option>
          </select>

          {/* Checkbox/Radio Answers */}
          {(question.type === "checkbox" || question.type === "radio") &&
            question.answers.map((answer, aIndex) => (
              <div key={aIndex} className="flex items-center mb-3">
                <input
                  type="text"
                  value={answer.text}
                  onChange={(e) => updateAnswerOption(qIndex, aIndex, e.target.value)}
                  placeholder="Enter answer option"
                  className="block w-full p-2 border border-gray-300 rounded-lg focus:ring focus:ring-indigo-200"
                />
                {/* Next Question ID (optional) */}
                <input
                  type="text"
                  placeholder="Next Question ID (optional)"
                  value={answer.nextQuestion || ""}
                  onChange={(e) => updateNextQuestion(qIndex, aIndex, e.target.value)}
                  className="ml-4 w-1/4  p-2 border border-gray-300 rounded-lg"
                />
                <button onClick={() => removeAnswerOption(qIndex, aIndex)} className="ml-2 text-red-500">
                  <FaTrashCan />
                </button>
              </div>
            ))}

          {/* Add Answer Option Button */}
          {(question.type === "checkbox" || question.type === "radio") && (
            <button
              onClick={() => addAnswerOption(qIndex)}
              className="block w-full p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700"
            >
              Add Answer Option
            </button>
          )}

          {/* Remove Question Button */}
          <button onClick={() => removeQuestion(qIndex)} className="mt-4 text-red-500 hover:text-red-700">
            Remove Question
          </button>

          {/* Attach Header to Question */}
          {headers.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <select
                value={headerSelections[question.id] || ""}
                onChange={(e) => setHeaderSelections((prev) => ({ ...prev, [question.id]: e.target.value }))}
                className="block p-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Header</option>
                {headers.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.title}
                  </option>
                ))}
              </select>
              <button onClick={() => attachHeaderToQuestion(qIndex)} className="px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-700">
                Add Header
              </button>
              {question.headerId && (
                <span className="text-sm text-gray-600">
                  Attached: {headers.find((h) => String(h.id) === String(question.headerId))?.title || "Unknown"}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-3">
        <FaArrowLeftLong
          className="cursor-pointer"
          onClick={() => {
            navigate("/qualitative-assessment-dashboard");
          }}
        />
        <h3 className="font-semibold text-lg">Create Dynamic Form</h3>
      </div>
      <div className="border-b mt-3 mb-4" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
      {/* Category & Subcategory Selection */}
      <div className="flex flex-col w-full p-5 mb-6 bg-white rounded-lg border border-gray-200">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
            {/* Category dropdown */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubCategory(""); // Reset sub-category when category changes
                  setQuestions([]); // Clear questions when category changes
                }}
                className="block w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Category</option>
                {Object.keys(categories).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Sub-Category dropdown */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Sub-Category
              </label>
              <select
                value={selectedSubCategory}
                onChange={(e) => setSelectedSubCategory(e.target.value)}
                className="block w-full p-2 border border-gray-300 rounded-lg"
                disabled={!selectedCategory}
              >
                <option value="">Select Sub-Category</option>
                {selectedCategory &&
                  categories[selectedCategory].map((subCategory) => (
                    <option key={subCategory} value={subCategory}>
                      {subCategory}
                    </option>
                  ))}
              </select>
            </div>

            {/* Year Filter */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-600">
                Select Year
              </label>
              <select
                name="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Year</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
               
              </select>
            </div>

            <div className="space-y-1">
              {" "}
              {/* Load Predefined Form */}
              <button
                onClick={updateFormForSelection}
                className="px-5 py-2 text-white bg-indigo-500 rounded-lg hover:bg-indigo-700 mt-6"
                disabled={!selectedSubCategory}
              >
                Load Form
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Header Sections */}
      <div className="flex flex-col w-full p-5 mb-6 bg-white rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-4">Sections</h4>
        {headers.map((h) => (
          <div key={h.id} className="mb-8 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2">
                {editingHeaderId === h.id ? (
                    <input
                      type="text"
                    autoFocus
                    value={editingHeaderTitle}
                    onChange={(e) => setEditingHeaderTitle(e.target.value)}
                    onBlur={commitEditHeader}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitEditHeader();
                      if (e.key === "Escape") cancelEditHeader();
                    }}
                    className="px-3 py-2 border border-gray-300 rounded w-64"
                  />
                ) : (
                  <h5 className="text-lg font-bold text-gray-800">{h.title}</h5>
                )}
                {editingHeaderId === h.id ? (
                  <>
                    <button
                      onClick={commitEditHeader}
                      className="px-3 py-1.5 text-white bg-green-600 hover:bg-green-700 rounded"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEditHeader}
                      className="px-3 py-1.5 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => startEditHeader(h)}
                    className="px-3 py-1.5 text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-50 rounded"
                  >
                    Edit
                  </button>
                )}
                  </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => addQuestionToHeader(h.id)}
                  className="px-3 py-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded"
                >
                  Add Question
                </button>
                <button
                  onClick={() => removeHeader(h.id)}
                  className="px-3 py-1.5 text-white bg-red-500 hover:bg-red-600 rounded"
                >
                  Remove Header
                </button>
              </div>
            </div>

            <div className="p-4">
              {questions.some((q) => q.headerId === h.id) ? (
                questions.map((question, qIndex) =>
                  question.headerId === h.id ? renderQuestionBlock(question, qIndex) : null
                )
              ) : (
                <div className="text-sm text-gray-500">No questions in this section yet.</div>
              )}
            </div>
          </div>
        ))}

        <div className="flex justify-center">
              <button
            onClick={addHeaderSection}
            className="inline-flex items-center gap-2 px-4 py-2 text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 rounded"
              >
            <FaPlus /> Add Header
              </button>
        </div>
      </div>
        </div>

        {/* Right: Real-time Logic Preview */}
        <div className="w-full">
          <div className="flex flex-col w-full p-5 mb-6 bg-white rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-4">Real-time Logic Preview</h4>
            <div className="h-[70vh] overflow-auto">
              <LogicViewer
                questions={questions.map((q) => ({
                  id: q.id,
                  question_text: q.text,
                  question_type: q.type,
                  headerName:
                    headers.find((h) => String(h.id) === String(q.headerId))?.title || null,
                  answers: (q.answers || []).map((a) => ({
                    answer_text: a.text,
                    next_question_id:
                      a.nextQuestion != null && a.nextQuestion !== "" && !isNaN(Number(a.nextQuestion))
                        ? Number(a.nextQuestion)
                        : null,
                  })),
                }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Questions are now rendered within their respective header sections */}

      {/* Add New Question Button */}
      {/* <button
        onClick={addQuestion}
        className="px-5 py-2 bg-green-500 text-white rounded-lg hover:bg-green-700"
      >
        Add Question
      </button> */}

      {/* Publish Form Button */}
      <button
        onClick={publishForm}
        className="ml-4 px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700"
      >
        Publish Form
      </button>
    </div>
  );
};

export default CreateForm;
